import React from 'react';
import {Box, Typography, Container, Grid, alpha, useTheme} from '@mui/material';
import {useTranslation} from "react-i18next";
import SpeedIcon from '@mui/icons-material/Speed';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SecurityIcon from '@mui/icons-material/Security';

const BenefitsSection = () => {
  const {t} = useTranslation();
  const theme = useTheme();

  const benefits = [
    {
      icon: SpeedIcon,
      title: t('Instant setup'),
      description: t('Launch your digital menu in under 3 minutes. No coding, no complicated setup, no waiting.'),
      color: '#10B981',
    },
    {
      icon: SavingsIcon,
      title: t('Lower costs'),
      description: t('Save money compared to expensive delivery apps. Pay a flat fee, keep your margins high.'),
      color: '#F59E0B',
    },
    {
      icon: TrendingUpIcon,
      title: t('Increase sales'),
      description: t('Serve more customers faster. No queues, no missed orders. Every customer can order instantly.'),
      color: '#8B5CF6',
    },
    {
      icon: PhonelinkIcon,
      title: t('No app needed'),
      description: t('Customers just scan and order. Works on any smartphone browser. No download required.'),
      color: '#3B82F6',
    },
    {
      icon: SupportAgentIcon,
      title: t('Tailored for Israel'),
      description: t('Hebrew support, local payment methods, local regulations. Built specifically for Israeli businesses.'),
      color: '#EC4899',
    },
    {
      icon: SecurityIcon,
      title: t('Secure & reliable'),
      description: t('Bank-level security. Your data is protected. Registered with Ministry of Justice.'),
      color: '#06B6D4',
    },
  ];

  return (
    <Box
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("WHY QRSELLER")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Built for small businesses")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {t("Simple, affordable, powerful. Everything you need to go digital without the complexity or cost.")}
          </Typography>
        </Box>

        {/* Benefits Grid */}
        <Grid container spacing={3}>
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: '12px',
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(benefit.color, 0.15)}`,
                      borderColor: alpha(benefit.color, 0.3),
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '10px',
                      bgcolor: alpha(benefit.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 24,
                        color: benefit.color,
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      color: 'text.primary',
                    }}
                  >
                    {benefit.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Comparison Callout */}
        <Box
          sx={{
            mt: 8,
            p: 5,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Why QRseller beats traditional delivery apps")}
          </Typography>
          <Grid container spacing={4} sx={{mt: 2}}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{fontWeight: 600, color: 'primary.main', mb: 1}}>
                {t("30% lower fees")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("Keep your profits instead of paying high commissions")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{fontWeight: 600, color: 'primary.main', mb: 1}}>
                {t("Full control")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("Your menu, your prices, your customer relationships")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{fontWeight: 600, color: 'primary.main', mb: 1}}>
                {t("Direct orders")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("No middleman. Customers order directly from you")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default BenefitsSection;
